import { CollectionReference, DocumentSnapshot, collection, doc, getDoc, getFirestore } from 'firebase/firestore';

import { toast } from '@/components/Toaster/Toast';

import { MappedSegmentTitle } from '@/models/kinematicModel.model';

import Logger from './CapcitorLog';
import {
  MappedSegment,
  SensorInDatabase,
  SensorInDatabaseWithTitle,
  SystemInDatabase,
} from './getFirebaseSystemByConfigId';

function mappedSegmentToSegment(mappedSegment: MappedSegment): MappedSegmentTitle {
  switch (mappedSegment) {
    case 'head':
      return 'Head';
    case 'rightArm':
      return 'Right Arm';
    case 'rightForearm':
      return 'Right Forearm';
    case 'rightHand':
      return 'Right Hand';
    case 'leftArm':
      return 'Left Arm';
    case 'leftForearm':
      return 'Left Forearm';
    case 'leftHand':
      return 'Left Hand';
    case 'trunk':
      return 'Trunk';
    case 'sacrum':
      return 'Sacrum';
    case 'leftPelvis':
      return 'Left Pelvis';
    case 'leftThigh':
      return 'Left Thigh';
    case 'leftCalf':
      return 'Left Calf';
    case 'leftFoot':
      return 'Left Foot';
    case 'rightPelvis':
      return 'Right Pelvis';
    case 'rightThigh':
      return 'Right Thigh';
    case 'rightCalf':
      return 'Right Calf';
    case 'rightFoot':
      return 'Right Foot';
    case 'undefined':
    default:
      return 'Undefined';
  }
}

export const getFirebaseSensorsBySystem = async (
  system: SystemInDatabase,
): Promise<Array<SensorInDatabaseWithTitle | undefined>> => {
  const firestore = getFirestore();
  const sensors = system?.sensors;

  if (!sensors) {
    toast({
      variant: 'error',
      title: 'Config does not contain sensors',
      description: 'Please contact support',
    });

    return [];
  }

  const sensorsRef = collection(firestore, 'sensors') as CollectionReference<SensorInDatabase>;
  const sensorsPromises: Promise<DocumentSnapshot<SensorInDatabase>>[] = [];

  sensors.forEach((sensor) => {
    sensorsPromises.push(getDoc(doc(sensorsRef, sensor)));
  });

  const sensorsResults = await Promise.allSettled(sensorsPromises);
  const sensorsInDatabase: Array<SensorInDatabaseWithTitle | undefined> = sensorsResults.map((result) => {
    if (result.status === 'fulfilled' && result.value.exists()) {
      const data = result.value.data();
      return {
        ...data,
        title: mappedSegmentToSegment(data.mappedSegment) ?? 'Unknown',
        // Only hub sensors have macAddress_LE
        // When it's defined that's the one that should be used!
        id: data.macAddress_LE ?? data.macAddress,
      };
    }

    toast({
      variant: 'error',
      title: 'Error fetching sensors',
      description: 'Please try again later',
    });

    return undefined;
  });

  Logger.info('Sensors in database:', sensorsInDatabase);

  return sensorsInDatabase;
};
